import { Injectable, Type } from '@angular/core';
import { AbcCore, CompareHelper, ProfileInfoViewModel } from '@barthauer/abc/core';
import { AbcConfigService } from '@barthauer/abc/core/configuration';
import { ResourceManagerService, ResourceSet } from '@barthauer/abc/core/resources';
import { ProfileInfo, ProfileService } from '@barthauer/basys/core/authentication';
import {
  WizardImportConfig,
  WizardImportContentPage,
} from 'libs/abc/interfaces/core/wizards/import/src/lib/models/config.model';
import { WorkerResult } from 'libs/abc/interfaces/core/wizards/import/src/lib/models/import.model';
import { WizardImportService } from 'libs/abc/interfaces/core/wizards/import/src/lib/wizard-import.service';
import { EMPTY, from, iif, Observable, of } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { AsciiImportQueryModel, AsciiWizardSettingDocument, AsciiWizardTransferObject } from './models/import.model';
import { SettingSelectionPageComponent } from './pages/setting-selection-page/setting-selection-page.component';
import { SettingShapeselectionPageComponent } from './pages/setting-shapeselection-page/setting-shapeselection-page.component';

@Injectable({
  providedIn: 'root',
})
export class AsciiImportService {
  private asciiImportWorkerUri: string;
  public resAsync: Promise<ResourceSet>;
  public res = ResourceSet.Empty;

  constructor(
    configService: AbcConfigService,
    private abcCore: AbcCore,
    private profileService: ProfileService,
    private wizardImportService: WizardImportService,
    private resourceManagerService: ResourceManagerService
  ) {
    this.asciiImportWorkerUri = configService.configuration.WebApi.ServiceURLs['ExtensionWorkersStartAsciiImport'];
    this.resAsync = this.resourceManagerService
      .getResourceSet('basys-interfaces-ascii-import')
      .then((resSet) => (this.res = resSet));
  }

  public loadWizard(): void {
    const config: WizardImportConfig = new WizardImportConfig();
    config.transferObject = new AsciiWizardTransferObject();
    config.importFileNameSchema = 'AsciiImport_' + WizardImportConfig.TimeStamp_Placeholder + '.zip';
    config.welcomePage.title = this.res.get('WizardTitle');
    config.welcomePage.description = this.res.get('WizardDes');

    config.contentPages.push(
      this.createWizardContentPage(
        this.res.get('ImportFile'),
        this.res.get('SelectImportFile'),
        SettingShapeselectionPageComponent
      )
    );

    config.contentPages.push(
      this.createWizardContentPage(
        this.res.get('Configuration'),
        this.res.get('SelectConfig'),
        SettingSelectionPageComponent
      )
    );

    config.startImportWorker = (transferObject) =>
      this.startAsciiImportWorker(<AsciiWizardTransferObject>transferObject);
    this.wizardImportService.loadWizard(config);
  }

  private createWizardContentPage(
    title: string,
    description: string,
    component: Type<unknown>
  ): WizardImportContentPage {
    const page: WizardImportContentPage = new WizardImportContentPage();
    page.title = title;
    page.description = description;
    page.component = component;

    return page;
  }

  public async getWizardSettingDocuments(): Promise<{
    allowCustomDocuments: boolean;
    documents: AsciiWizardSettingDocument[];
  }> {
    const profileInfo = await this.profileService.getCurrentProfile(true).toPromise();
    if (profileInfo?.settings) {
      const vm = new ProfileInfoViewModel(profileInfo);
      return vm.settings?.findCommandDocuments('Import_Ascii', 'Settings');
    }
    return { allowCustomDocuments: false, documents: [] };
  }

  private startAsciiImportWorker(transferObject: AsciiWizardTransferObject): Observable<WorkerResult> {
    return from(this.transferZipfileToBase64String(transferObject.shapeFileZipped)).pipe(
      mergeMap((zipFileBase64) => {
        const query: AsciiImportQueryModel = new AsciiImportQueryModel();
        query.BCEDocumentId = transferObject.settingDocument.id;
        query.ShapezipfileAsBase64 = zipFileBase64;

        return this.abcCore.data.executeWebApiPOSTMethod(this.asciiImportWorkerUri, query, true).pipe(
          map((data) => (!CompareHelper.isEmptyObject(data) ? (data as any) : null)),
          map((data) => new WorkerResult(data))
        );
      }),
      catchError((error) => {
        this.abcCore.error.publish(error);
        return EMPTY;
      })
    );
  }

  private transferZipfileToBase64String(shapeFileZipped: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.onload = () => {
        const base64Data = reader.result as string;
        resolve(base64Data);
      };

      reader.onerror = (error) => {
        reject(error);
      };

      reader.readAsDataURL(shapeFileZipped);
    });
  }
}
